// Lab Issues — digital-edition pipeline state.
//
// The four states have different owners, and the UI's job is to make that
// obvious: missing_source needs a human to upload a PDF (no button can fix
// it), while awaiting_render resolves itself on the next drain or the 02:20
// cron.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { AddIssueDialog } from './AddIssueDialog'
import { MemberIssueLink } from './MemberPageLink'
import { ISSUE_STATUS_CLASS, ISSUE_STATUS_LABEL, issueActionError } from './usePublishIssue'
import { usePrintReadyBulk } from './usePrintReady'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'

type Job = { id: number; sku: string | null; publication_slug: string }
type Skipped = {
  edition_id: number
  title: string | null
  reason: string
  message: string
}
type BulkResult = { queued: Job[]; skipped: Skipped[]; dropped: number }

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const STATE_LABEL: Record<string, string> = {
  rendered: 'Rendered',
  awaiting_render: 'Awaiting render',
  missing_source: 'No PDF on EFS',
  orphan_pdf: 'PDF with no issue',
}

const STATE_CLASS: Record<string, string> = {
  rendered: 'text-paper-trace',
  awaiting_render: 'text-amber-glow',
  missing_source: 'text-red-400',
  orphan_pdf: 'text-amber-glow/70',
}

type IssueRow = {
  id: number | null
  publication_slug: string
  title: string | null
  post_date: string | null
  sku: string | null
  state: string
  pdf_page_count: number | null
  pages_base_url: string | null
  viewer_url: string | null
  source_path: string | null
  source_size_bytes: number | null
  // Publication state, distinct from `state` (the render pipeline).
  status?: string
  origin?: string
}

type IssueList = { items: IssueRow[]; orphans: IssueRow[] }
type Stats = {
  by_state: Record<string, number>
  by_publication: Record<string, Record<string, number>>
  stuck_recent: number
  window_months: number
  inventory_last_seen_at: string | null
  oldest_pending_job_at: string | null
  inventory_stale_hours: number
  pending_warn_minutes: number
  sku_conflicts: number
  sku_duplicate_posts: number
  sku_drift: number
}

type SortKey =
  | 'title'
  | 'publication_slug'
  | 'sku'
  | 'post_date'
  | 'pdf_page_count'
  | 'state'

type Column = { key: SortKey; label: string; numeric?: boolean }

const COLUMNS: Column[] = [
  { key: 'title', label: 'Issue' },
  { key: 'publication_slug', label: 'Publication' },
  { key: 'sku', label: 'SKU' },
  { key: 'post_date', label: 'Published', numeric: true },
  { key: 'pdf_page_count', label: 'Pages', numeric: true },
  { key: 'state', label: 'State' },
]

/** Keys where "biggest first" is the more useful initial direction. */
const NUMERIC_KEYS = new Set<SortKey>(['post_date', 'pdf_page_count'])

/** Comparable value for a row, or null when the cell is empty.
 *
 * Publication and state sort by their DISPLAYED label, not the raw slug —
 * sorting "Publication" should order the column the reader sees ("Die Cast X",
 * "Flight Journal", ...), not the underlying slugs (diecast, flight_journal),
 * which happen to differ in order.
 */
function sortValue(r: IssueRow, key: SortKey): string | number | null {
  switch (key) {
    case 'title':
      return r.title?.toLowerCase() ?? null
    case 'publication_slug':
      return (PUB_NAMES[r.publication_slug] ?? r.publication_slug).toLowerCase()
    case 'sku':
      return r.sku?.toLowerCase() ?? null
    case 'post_date':
      // ISO-8601 strings sort lexicographically in chronological order.
      return r.post_date ?? null
    case 'pdf_page_count':
      return r.pdf_page_count ?? null
    case 'state':
      return (STATE_LABEL[r.state] ?? r.state).toLowerCase()
  }
}

function SortHeader({
  col, active, dir, onSort,
}: {
  col: Column
  active: boolean
  dir: 'asc' | 'desc'
  onSort: (k: SortKey) => void
}) {
  return (
    <th className={col.key === 'title' ? 'py-2' : undefined}>
      <button
        type="button"
        onClick={() => onSort(col.key)}
        className={`label inline-flex items-center gap-1 hover:text-amber-glow ${
          active ? 'text-amber-glow' : ''
        }`}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={`Sort by ${col.label}`}
      >
        {col.label}
        <span className="font-mono text-[9px]">
          {active ? (dir === 'asc' ? '\u25B2' : '\u25BC') : '\u2195'}
        </span>
      </button>
    </th>
  )
}

function agoMinutes(iso: string | null): number | null {
  if (!iso) return null
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return null
  return (Date.now() - then) / 60000
}

function humanAgo(mins: number): string {
  if (mins < 1) return 'just now'
  if (mins < 60) return `${Math.round(mins)} min ago`
  const hrs = mins / 60
  if (hrs < 48) return `${Math.round(hrs)}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

/** Pipeline liveness.
 *
 * This whole feature exists because a nightly job exited rc=0 for three
 * mornings while rendering nothing, and nothing surfaced it. The counts above
 * report stuck ISSUES; this strip reports a stuck PIPELINE, which is the
 * failure that stays invisible longest — if the nightly dies, no new issue
 * gets stuck until one is published, which can be weeks away.
 */
/** SKU integrity.
 *
 * A conflict means two DIFFERENT editions resolve to one SKU, so the renderer
 * matches both to the same PDF and one shows the wrong magazine. That is not
 * hypothetical: RC Car Action's March 2025 issue served the March 2024
 * magazine until 2026-08-21, and the only visible symptom was an "orphan"
 * PDF, which reads like a filing problem rather than a content error.
 *
 * Duplicates are deliberately reported separately and quietly — the same
 * issue posted twice in WordPress, correct content, listed twice. There are
 * 14 of those in RCCA; showing them at conflict weight would bury the one
 * that matters.
 */
function Integrity({ stats }: { stats: Stats }) {
  if (!stats.sku_conflicts && !stats.sku_drift && !stats.sku_duplicate_posts) {
    return null
  }
  return (
    <div className="font-mono text-[11px] space-y-1">
      {stats.sku_conflicts > 0 && (
        <div className="text-red-400">
          {stats.sku_conflicts} SKU conflict{stats.sku_conflicts === 1 ? '' : 's'}
          {' '}— two different issues map to one PDF, so one of them is showing
          the wrong magazine. Needs a human.
        </div>
      )}
      {stats.sku_drift > 0 && (
        <div className="text-amber-glow">
          {stats.sku_drift} issue{stats.sku_drift === 1 ? '' : 's'} whose stored
          SKU no longer matches its title — a title was corrected in WordPress;
          re-run the render to re-derive.
        </div>
      )}
      {stats.sku_duplicate_posts > 0 && (
        <div className="text-paper-trace">
          {stats.sku_duplicate_posts} duplicate WordPress posts (same issue
          published twice — content is correct, it just lists twice).
        </div>
      )}
    </div>
  )
}

function Liveness({ stats }: { stats: Stats }) {
  const invMins = agoMinutes(stats.inventory_last_seen_at)
  const invStale = invMins !== null && invMins > stats.inventory_stale_hours * 60
  const invUnknown = invMins === null

  const pendMins = agoMinutes(stats.oldest_pending_job_at)
  const pendLate = pendMins !== null && pendMins > stats.pending_warn_minutes

  return (
    <div className="flex flex-wrap gap-x-8 gap-y-1 font-mono text-[11px]">
      <span className={invStale || invUnknown ? 'text-red-400' : 'text-paper-trace'}>
        EFS inventory refreshed:{' '}
        {invUnknown ? 'never' : humanAgo(invMins)}
        {invStale && ' — the 02:20 render may have stopped running'}
        {invUnknown && ' — the renderer has never recorded a scan'}
      </span>
      <span className={pendLate ? 'text-red-400' : 'text-paper-trace'}>
        Oldest queued job:{' '}
        {pendMins === null ? 'none queued' : humanAgo(pendMins)}
        {pendLate && ' — the 5-minute drain may have stopped running'}
      </span>
    </div>
  )
}

/** Mark a date range printable, or take one back off.
 *
 *  The archive is over a thousand active issues and an operator marking a
 *  decade one at a time will not finish. The cutoff they actually hold in
 *  their head — "everything since we changed production" — is what this
 *  takes.
 *
 *  Marking ON skips retired issues and anything with no PDF: there is
 *  nothing to print from, and selling an order nobody can fulfil is the
 *  failure the flag exists to prevent. Marking OFF has neither exclusion,
 *  so a decade judged unusable comes off in one action whatever state its
 *  rows are in. The API enforces both; this only says so.
 */
function PrintReadyBulkPanel({ pubFilter }: { pubFilter: string }) {
  const [fromDate, setFromDate] = useState('2020-01-01')
  const [toDate, setToDate] = useState('')
  const [on, setOn] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const bulk = usePrintReadyBulk()

  const scope = pubFilter ? (PUB_NAMES[pubFilter] ?? pubFilter) : 'all publications'
  const range = `${fromDate || 'the beginning'} to ${toDate || 'now'}`

  return (
    <Panel label="Printable in bulk" accent="none">
      <p className="label mb-3 text-paper-trace">
        Whether a printed copy can be produced — a different question from
        publishing. Most of the archive is scans, and nothing in the file's
        metadata tells a scan from a print-ready PDF, so this is a judgement
        only you can make. Issues with no PDF are never marked printable.
      </p>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="grid gap-1">
          <span className="label">From</span>
          <input
            type="date" value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setConfirming(false) }}
            className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
          />
        </label>
        <label className="grid gap-1">
          <span className="label">To (optional)</span>
          <input
            type="date" value={toDate}
            onChange={(e) => { setToDate(e.target.value); setConfirming(false) }}
            className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
          />
        </label>
        <label className="flex items-center gap-2 pb-2">
          <input
            type="checkbox" checked={on}
            onChange={(e) => { setOn(e.target.checked); setConfirming(false) }}
          />
          <span className="label">{on ? 'Mark printable' : 'Mark NOT printable'}</span>
        </label>

        {confirming ? (
          <div className="flex gap-2 items-center flex-wrap pb-1">
            <span className="label text-amber-glow">
              {on ? 'Offer' : 'Withdraw'} {range}, {scope}?
            </span>
            <Button size="sm" disabled={bulk.isPending}
                    onClick={() => {
                      bulk.mutate({
                        print_ready: on,
                        from_date: fromDate || undefined,
                        to_date: toDate || undefined,
                        publication_slug: pubFilter || undefined,
                      })
                      setConfirming(false)
                    }}>
              {bulk.isPending ? 'Working…' : 'Yes, do it'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="mb-1"
                  onClick={() => setConfirming(true)}>
            Apply to {scope}
          </Button>
        )}
      </div>

      {bulk.isSuccess && (
        <p className="label mt-3 text-radar-green">
          {bulk.data.updated} {bulk.data.updated === 1 ? 'issue' : 'issues'}{' '}
          {on ? 'can now be printed' : 'are no longer offered in print'}.
          {bulk.data.updated === 0 && ' Nothing matched, or they were already set that way.'}
        </p>
      )}
      {bulk.isError && (
        <p className="label mt-3 text-radar-red">
          {issueActionError(bulk.error) ?? 'That did not work.'}
        </p>
      )}
    </Panel>
  )
}

export function LabIssuesPage() {
  const [pubFilter, setPubFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')

  // Default: newest published first. Operator-requested (2026-08-20).
  const [sortKey, setSortKey] = useState<SortKey>('post_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      // Dates and counts read most usefully largest-first; text A-Z.
      setSortDir(NUMERIC_KEYS.has(key) ? 'desc' : 'asc')
    }
  }

  const stats = useQuery({
    queryKey: ['lab', 'issues', 'stats'],
    queryFn: () => api.get<Stats>('/api/lab/issues/stats'),
    staleTime: 30_000,
  })

  const list = useQuery({
    queryKey: ['lab', 'issues', 'list', pubFilter, stateFilter],
    queryFn: () => {
      const p = new URLSearchParams()
      if (pubFilter) p.set('publication_slug', pubFilter)
      if (stateFilter) p.set('state', stateFilter)
      const qs = p.toString()
      return api.get<IssueList>(`/api/lab/issues${qs ? `?${qs}` : ''}`)
    },
  })

  const qc = useQueryClient()
  const orphans = list.data?.orphans ?? []

  // Selection is by edition id and survives re-sorts, but is cleared when
  // the filters change — acting on rows you can no longer see is how a
  // bulk action surprises someone.
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // `adopting` carries an orphan's identity into the dialog; undefined means
  // a brand-new issue, which needs the file-upload step.
  const [adding, setAdding] = useState(false)
  const [adopting, setAdopting] =
    useState<{ sku: string; pub: string } | undefined>(undefined)
  const [result, setResult] = useState<BulkResult | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const runAction = <T,>(fn: () => Promise<T>, onDone: (r: T) => void) => {
    setActionError(null)
    setResult(null)
    return fn().then(onDone).catch((e: unknown) => {
      const msg = e instanceof ApiError
        ? (((e.detail as { detail?: { message?: string } })?.detail?.message)
           ?? `Error ${e.status}`)
        : 'Request failed'
      setActionError(msg)
    })
  }

  const afterQueue = (r: BulkResult) => {
    setResult(r)
    setSelected(new Set())
    qc.invalidateQueries({ queryKey: ['lab', 'issues'] })
  }

  const renderSelected = useMutation({
    mutationFn: () => runAction(
      () => api.post<BulkResult>('/api/lab/issues/render-bulk',
                                 { edition_ids: [...selected] }),
      afterQueue,
    ),
  })

  const renderPending = useMutation({
    mutationFn: () => runAction(
      () => api.post<BulkResult>('/api/lab/issues/render-pending',
                                 { publication_slug: pubFilter }),
      afterQueue,
    ),
  })

  const resync = useMutation({
    mutationFn: () => runAction(
      () => api.post<Job>('/api/lab/issues/resync',
                          { publication_slug: pubFilter }),
      () => {
        setResult({ queued: [], skipped: [], dropped: 0 })
        qc.invalidateQueries({ queryKey: ['lab', 'issues'] })
      },
    ),
  })

  const busy = renderSelected.isPending || renderPending.isPending || resync.isPending

  // Sorting is client-side: the endpoint is unpaginated (~1,109 rows), so a
  // sort is instant and costs no round-trip. This deliberately overrides the
  // API's problems-first ordering — the operator asked for date order by
  // default. Problems stay reachable via the state filter and the clickable
  // stat counts above, which matters because 5 missing_source rows are
  // otherwise invisible among 1,093 rendered ones.
  const rows = useMemo(() => {
    const items = [...(list.data?.items ?? [])]
    const dir = sortDir === 'asc' ? 1 : -1
    return items.sort((a, b) => {
      const av = sortValue(a, sortKey)
      const bv = sortValue(b, sortKey)
      // Nulls always sort last regardless of direction — an issue with no
      // SKU or no page count shouldn't top the list just because you clicked
      // that column.
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      // Stable tiebreak so equal values don't reshuffle between renders.
      return (a.id ?? 0) - (b.id ?? 0)
    })
  }, [list.data, sortKey, sortDir])

  // Filters change what is on screen; a selection made against the old view
  // would act on rows the operator can no longer see.
  useEffect(() => { setSelected(new Set()) }, [pubFilter, stateFilter])

  // Only rows a render can actually be queued for — the same two guards the
  // API applies, so "select all" never ticks a row that comes back skipped.
  const selectableIds = useMemo(
    () => rows
      .filter((r) => r.id != null && r.sku !== null
                     && r.state !== 'missing_source')
      .map((r) => r.id as number),
    [rows],
  )

  // Scoped to the selected title, matching the button that uses it.
  const pendingCount = pubFilter
    ? (stats.data?.by_publication?.[pubFilter]?.awaiting_render ?? 0)
    : 0

  // The stats endpoint already returns per-publication counts, so scoping the
  // header to the selected title needs no refetch — just a different slice.
  const scopedCounts: Record<string, number> = pubFilter
    ? (stats.data?.by_publication?.[pubFilter] ?? {})
    : (stats.data?.by_state ?? {})

  const scopeLabel = pubFilter
    ? (PUB_NAMES[pubFilter] ?? pubFilter)
    : 'All Titles'

  return (
    <div className="p-8 space-y-6 max-w-[1500px]">
      <header className="flex items-baseline justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label">LAB · L6</div>
          <h1 className="font-serif text-[26px] text-paper mt-1">
            Issues — {scopeLabel}
          </h1>
          <div className="label mt-1">Digital-edition pipeline</div>
        </div>
        <div className="flex gap-6">
          {stats.data ? (
            // Clickable so problems stay one click away. The default sort is
            // by published date (operator-requested), which buries the 5
            // missing_source rows among 1,093 rendered ones — these counts are
            // what keeps them reachable. Clicking the active one clears it.
            (['missing_source', 'awaiting_render', 'rendered', 'orphan_pdf'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStateFilter((cur) => (cur === s ? '' : s))}
                className={`text-left hover:opacity-80 ${
                  stateFilter === s ? 'underline underline-offset-4' : ''
                }`}
                title={stateFilter === s ? 'Clear this filter' : `Show only: ${STATE_LABEL[s]}`}
              >
                <div className="label">{STATE_LABEL[s]}</div>
                <div className={`font-mono text-[20px] tabular-nums ${STATE_CLASS[s]}`}>
                  {scopedCounts[s] ?? 0}
                </div>
              </button>
            ))
          ) : (
            <div className="label text-paper-trace animate-pulse">Loading…</div>
          )}
        </div>
      </header>

      {stats.data && <Liveness stats={stats.data} />}
      {stats.data && <Integrity stats={stats.data} />}

      <PrintReadyBulkPanel pubFilter={pubFilter} />

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="label block mb-1">Publication</label>
          <select
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value)}
            className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
          >
            <option value="">All publications</option>
            {Object.entries(PUB_NAMES).map(([slug, name]) => (
              <option key={slug} value={slug}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label block mb-1">State</label>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
          >
            <option value="">All states</option>
            {Object.entries(STATE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Title-scoped actions. Both jobs take exactly one publication, so
          they stay disabled on "All publications" rather than guessing. */}
      <div className="flex flex-wrap gap-3 items-center border-t border-ink-500/40 pt-4">
        <button
          type="button"
          disabled={!pubFilter || busy}
          onClick={() => resync.mutate()}
          title={pubFilter
            ? 'Re-read WordPress and rebuild this title\u2019s issue rows'
            : 'Pick a publication first — this job runs for one title'}
          className="px-3 py-1.5 border border-paper-trace text-paper font-mono text-[11px] tracking-label uppercase hover:border-paper disabled:opacity-30"
        >
          {resync.isPending ? 'Queueing…' : 'Resync metadata'}
        </button>

        <button
          type="button"
          disabled={!pubFilter || busy || pendingCount === 0}
          onClick={() => renderPending.mutate()}
          title={!pubFilter
            ? 'Pick a publication first — this job runs for one title'
            : pendingCount === 0
              ? 'Nothing is waiting to render in this title'
              : 'Queue a render for every issue that has a PDF but no pages'}
          className="px-3 py-1.5 border border-paper-trace text-paper font-mono text-[11px] tracking-label uppercase hover:border-paper disabled:opacity-30"
        >
          {renderPending.isPending
            ? 'Queueing…'
            : `Render pending (${pendingCount})`}
        </button>

        <button
          type="button"
          disabled={selected.size === 0 || busy}
          onClick={() => renderSelected.mutate()}
          title="Queue a render for the ticked rows — already-rendered ones are forced"
          className="px-3 py-1.5 bg-amber-glow text-paper-deepest font-mono text-[11px] tracking-label uppercase disabled:opacity-30"
        >
          {renderSelected.isPending
            ? 'Queueing…'
            : `Render selected (${selected.size})`}
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={() => { setAdopting(undefined); setAdding(true) }}
          title="Upload a PDF and create its issue, without WordPress"
          className="px-3 py-1.5 border border-radar-green/60 text-radar-green font-mono text-[11px] tracking-label uppercase hover:bg-radar-green/10 disabled:opacity-30"
        >
          + Add issue
        </button>

        <span className="font-mono text-[10px] text-paper-trace">
          The drain claims one job every 5 minutes.
        </span>
      </div>

      {actionError && (
        <div className="border border-radar-red/50 text-radar-red font-mono text-[11px] p-3">
          {actionError}
        </div>
      )}

      {result && <BulkSummary result={result} />}

      {list.isLoading ? (
        <Loading />
      ) : rows.length === 0 && orphans.length === 0 ? (
        // Selecting the "PDF with no issue" filter legitimately returns
        // zero `items` — orphans live in a separate response key — so the
        // empty state must not fire while a populated orphan list is about
        // to render below it.
        <Empty text="No issues match these filters" />
      ) : rows.length === 0 ? null : (
        <table className="w-full text-left">
          <thead>
            <tr className="label border-b border-ink-500">
              <th className="w-8 py-2">
                <input
                  type="checkbox"
                  aria-label="Select every issue shown"
                  checked={selectableIds.length > 0
                           && selected.size === selectableIds.length}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = selected.size > 0
                        && selected.size < selectableIds.length
                    }
                  }}
                  onChange={(e) => setSelected(
                    e.target.checked ? new Set(selectableIds) : new Set(),
                  )}
                />
              </th>
              {COLUMNS.map((c) => (
                <SortHeader
                  key={c.key}
                  col={c}
                  active={sortKey === c.key}
                  dir={sortDir}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-ink-500/40">
                <td className="py-2">
                  {/* No box on rows nothing can be queued for: no SKU means
                      a render cannot be targeted, and missing_source has no
                      PDF to render. Both would come back as `skipped`. */}
                  {r.id != null && r.sku !== null
                   && r.state !== 'missing_source' ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.title ?? r.sku}`}
                      checked={selected.has(r.id)}
                      onChange={(e) => setSelected((cur) => {
                        const next = new Set(cur)
                        if (e.target.checked) next.add(r.id as number)
                        else next.delete(r.id as number)
                        return next
                      })}
                    />
                  ) : null}
                </td>
                <td className="py-2 font-serif text-paper">
                  <span className="inline-flex items-center gap-2">
                    <Link to={`/lab/issues/${r.id}`} className="hover:text-amber-glow">
                      {r.title}
                    </Link>
                    <MemberIssueLink
                      publicationSlug={r.publication_slug}
                      viewerUrl={r.viewer_url}
                      status={r.status}
                      rendered={Boolean(r.pages_base_url)}
                    />
                  </span>
                </td>
                <td className="font-mono text-[11px] text-paper-dim">
                  {PUB_NAMES[r.publication_slug] ?? r.publication_slug}
                </td>
                <td className="font-mono text-[11px] text-paper-dim">{r.sku ?? '—'}</td>
                <td className="font-mono text-[11px] text-paper-dim tabular-nums">
                  {r.post_date ? r.post_date.slice(0, 10) : '—'}
                </td>
                <td className="font-mono text-[11px] text-paper-dim tabular-nums">
                  {r.pdf_page_count ?? '—'}
                </td>
                <td className="font-mono text-[11px]">
                  <span className={STATE_CLASS[r.state]}>
                    {STATE_LABEL[r.state]}
                  </span>
                  {/* Publication state is a different question from render
                      state: a rendered issue can still be a draft. Only
                      shown when it is not the ordinary 'published'. */}
                  {r.status && r.status !== 'active' && (
                    <span
                      className={`ml-2 text-[9px] tracking-label uppercase border px-1.5 py-0.5 ${
                        ISSUE_STATUS_CLASS[r.status] ?? 'border-paper-trace text-paper-trace'
                      }`}
                    >
                      {ISSUE_STATUS_LABEL[r.status] ?? r.status}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddIssueDialog
        open={adding}
        onClose={() => setAdding(false)}
        existingSku={adopting?.sku}
        existingPub={adopting?.pub}
      />

      {orphans.length > 0 && (
        <section>
          <h2 className="font-serif text-[18px] text-paper mb-2">
            PDFs with no issue row
          </h2>
          <p className="label mb-3">
            On EFS but not published in WordPress — check whether the issue
            was ever posted.
          </p>
          <ul className="font-mono text-[11px] text-paper-dim space-y-1">
            {orphans.map((o) => (
              <li
                key={`${o.publication_slug}-${o.sku}`}
                className="flex items-baseline gap-2 flex-wrap"
              >
                <button
                  type="button"
                  disabled={busy || !o.sku}
                  onClick={() => {
                    setAdopting({ sku: o.sku as string, pub: o.publication_slug })
                    setAdding(true)
                  }}
                  title="Create an issue row for this PDF and queue its render"
                  className="text-[10px] tracking-label uppercase border border-radar-green/60 text-radar-green px-1.5 py-0.5 hover:bg-radar-green/10 disabled:opacity-30"
                >
                  Ingest
                </button>
                <span>
                  {PUB_NAMES[o.publication_slug] ?? o.publication_slug} · {o.sku}
                </span>
                <span className="text-paper-trace">— {o.source_path}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/** What a bulk request actually did. A queue action that reports only
 *  success hides the rows it could not take — the reason a batch is
 *  summarised rather than merely acknowledged. */
function BulkSummary({ result }: { result: BulkResult }) {
  const { queued, skipped, dropped } = result
  if (!queued.length && !skipped.length && !dropped) {
    return (
      <div className="border border-ink-500 text-paper-dim font-mono text-[11px] p-3">
        Queued. The drain picks it up within 5 minutes.
      </div>
    )
  }
  return (
    <div className="border border-ink-500 p-3 space-y-2 font-mono text-[11px]">
      <div className="text-radar-green">
        {queued.length} job{queued.length === 1 ? '' : 's'} queued
        {queued.length > 0 && ' — roughly '}
        {queued.length > 0 && `${Math.max(5, queued.length * 5)} minutes`}
        {queued.length > 0 && ' to work through at one per 5-minute tick.'}
      </div>
      {dropped > 0 && (
        <div className="text-amber-glow">
          {dropped} row{dropped === 1 ? '' : 's'} beyond the per-request cap
          were not queued. Run the action again to take the rest.
        </div>
      )}
      {skipped.length > 0 && (
        <div className="space-y-1">
          <div className="text-amber-glow">
            {skipped.length} skipped:
          </div>
          <ul className="text-paper-trace space-y-0.5 pl-3">
            {skipped.map((sk) => (
              <li key={sk.edition_id}>
                {sk.title ?? `#${sk.edition_id}`} — {sk.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Loading() {
  return <div className="label text-paper-trace animate-pulse py-8 text-center">LOADING</div>
}

function Empty({ text }: { text: string }) {
  return <div className="label text-paper-trace py-8 text-center">{text}</div>
}
